import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { useUiStore } from '@/shared/lib/uiStore';
import {
  findAnchor,
  resolveOnPage,
  tourText,
  type ResolvedStep,
  type Tour,
  type TourSection,
  type TourStep,
  type TourStepContext,
} from '@/features/onboarding-tour';
import { PORTAL_PRIMARY } from '@/pages/MyTicketsPortal/nav-config';
import { TourCard } from './TourCard';
import { useSpotlight } from './use-spotlight';
import { useTourContext } from './use-tour-context';

const SHEET_QUERY = '(max-width: 768px)';
const WAIT_MS = 2500;
const SETTLE_MS = 400;

type Beat =
  | { kind: 'step'; step: TourStep; place: string; counter: string }
  | { kind: 'go'; section: TourSection; first: boolean; counter: string }
  | { kind: 'menu'; counter: string };

function planBeats(tour: Tour, ctx: TourStepContext): Beat[] {
  const beats: Beat[] = [];
  const triggers = new Set(tour.sections.map((s) => s.trigger).filter(Boolean));
  for (const step of tour.steps) {
    if (step.inMenu || (step.anchor && triggers.has(step.anchor))) continue;
    beats.push({ kind: 'step', step, place: 'intro', counter: 'Знакомство' });
  }
  const sections = tour.sections.filter((s) => findAnchor(s.trigger ?? `nav:${s.path}`) != null);
  const firstInMenu = sections.find((s) => !s.trigger);
  sections.forEach((section, i) => {
    const counter = `Раздел ${i + 1} из ${sections.length} · ${section.title}`;
    beats.push({ kind: 'go', section, first: section === firstInMenu, counter });
    for (const step of section.steps) {
      if (step.when && !step.when(ctx)) continue;
      beats.push({ kind: 'step', step, place: section.path, counter });
    }
  });
  beats.push({ kind: 'menu', counter: 'Напоследок' });
  for (const step of tour.outro) beats.push({ kind: 'step', step, place: 'outro', counter: 'Напоследок' });
  return beats;
}

const onSection = (section: TourSection, path: string) =>
  path === section.path ||
  (section.prefix === true && path.startsWith(`${section.path}/`)) ||
  (section.also ?? []).some((p) => path === p || path.startsWith(`${p}/`));

function sectionHint(path: string): string | null {
  const hint = PORTAL_PRIMARY.find((l) => l.to === path)?.hint;
  return hint ? hint[0].toLowerCase() + hint.slice(1) : null;
}

function goText(section: TourSection, first: boolean, menuOpen: boolean): string {
  if (section.trigger) return 'Нажмите «Обращение» вверху — так подаётся любая заявка в ИТ.';
  if (!menuOpen) {
    return first
      ? 'Все разделы портала собраны в меню. Откройте его — нажмите на подсвеченную кнопку.'
      : `Дальше — «${section.title}». Откройте меню.`;
  }
  const hint = sectionHint(section.path);
  return `Нажмите «${section.title}»${hint ? ` — ${hint}` : ''}.`;
}

export function MenuTourRun({ tour, onFinish }: { tour: Tour; onFinish: () => void }) {
  const ctx = useTourContext();
  const sheet = useMediaQuery(SHEET_QUERY);
  const menuOpen = useUiStore((s) => s.mobileNavOpen);
  const setMobileNav = useUiStore((s) => s.setMobileNav);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [beats] = useState(() => planBeats(tour, ctx));
  const [index, setIndex] = useState(0);
  const [resolved, setResolved] = useState<Record<number, ResolvedStep | null>>({});
  const popupRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const sawMenuRef = useRef(false);

  const beat = beats[index] as Beat | undefined;
  const advance = () => setIndex((i) => i + 1);

  useEffect(() => {
    sawMenuRef.current = false;
  }, [index]);

  useEffect(() => {
    if (beat?.kind !== 'step' || index in resolved) return;
    const started = performance.now();
    const settle = beats[index - 1]?.kind === 'go' ? SETTLE_MS : 0;
    let frame = 0;
    const poll = () => {
      if (performance.now() - started < settle) {
        frame = requestAnimationFrame(poll);
        return;
      }
      const r = resolveOnPage(beat.step, ctxRef.current, (a) => findAnchor(a) != null);
      if (r === 'wait' && performance.now() - started < WAIT_MS) {
        frame = requestAnimationFrame(poll);
        return;
      }
      setResolved((prev) => ({ ...prev, [index]: r === 'wait' ? null : r }));
    };
    poll();
    return () => cancelAnimationFrame(frame);
  }, [beat, beats, index, resolved]);

  useEffect(() => {
    if (beat?.kind === 'step' && resolved[index] === null) advance();
  }, [beat, index, resolved]);

  useEffect(() => {
    if (!beat) {
      onFinish();
      return;
    }
    if (beat.kind === 'go') {
      if (menuOpen) sawMenuRef.current = true;
      else if ((sawMenuRef.current || beat.section.trigger) && onSection(beat.section, pathname)) advance();
    } else if (beat.kind === 'menu') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (menuOpen) advance();
    } else if (beat.place === 'outro' && !menuOpen) {
      setIndex(beats.findIndex((b) => b.kind === 'menu'));
    }
  }, [beat, beats, menuOpen, pathname, onFinish]);

  const step = beat?.kind === 'step' ? resolved[index] : undefined;
  const anchor =
    beat?.kind === 'go'
      ? beat.section.trigger
        ? beat.section.trigger
        : menuOpen
          ? `nav:${beat.section.path}`
          : 'menu-button'
      : beat?.kind === 'menu'
        ? 'menu-button'
        : step?.spot
          ? step.anchor
          : null;

  useSpotlight(anchor, { popupRef, side: 'bottom', sheet, scroll: 'center' });

  if (!beat) return null;

  const interactive = beat.kind !== 'step';
  const last = index === beats.length - 1;

  let back: number | null = null;
  if (beat.kind === 'step') {
    let p = index - 1;
    while (p >= 0 && resolved[p] === null) p--;
    const prev = beats[p];
    if (prev?.kind === 'step' && prev.place === beat.place && resolved[p]) back = p;
  }

  let title: string;
  let body: string;
  let nextLabel = last ? 'Готово' : 'Далее';
  let onNext = advance;
  if (beat.kind === 'go') {
    title = beat.section.title;
    body = goText(beat.section, beat.first, menuOpen);
    nextLabel = 'Открыть';
    onNext = () => {
      sawMenuRef.current = true;
      if (beat.section.trigger) navigate(beat.section.path);
      else if (!menuOpen) setMobileNav(true);
      else {
        setMobileNav(false);
        navigate(beat.section.path);
      }
    };
  } else if (beat.kind === 'menu') {
    title = 'Профиль';
    body = 'Осталось последнее — оно внизу меню. Откройте его.';
    nextLabel = 'Открыть';
    onNext = () => setMobileNav(true);
  } else if (step) {
    title = step.title;
    body = tourText(step.body, ctx);
  } else {
    title = beat.counter;
    body = 'Открываем раздел…';
  }

  return (
    <TourCard
      popupRef={popupRef}
      nextRef={nextRef}
      modal={false}
      sheet={sheet}
      anchored={!sheet && anchor != null}
      pass={interactive}
      counter={beat.counter}
      title={title}
      body={body}
      nextLabel={nextLabel}
      onNext={onNext}
      busy={beat.kind === 'step' && !step}
      onBack={back != null ? () => setIndex(back) : undefined}
      onSkip={last ? undefined : onFinish}
      onClose={onFinish}
    />
  );
}
